import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FaHeart } from 'react-icons/fa';
import { MdModeComment } from 'react-icons/md';

import Humanograma from 'components/Humanograma';
import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { getKPIHistoryPag } from 'services/dashboard.service';
import { registerInteraction } from 'services/interactions.service';
import { convertEmojis } from 'utils/helpers';

import { IconLeafSeed, IconReplay, IconSprout, NivelAnimation } from 'components/rebrand/icons';
import { dimensaoByBehavior, hexToRgba, nivelByMoeda } from 'components/rebrand/mappings';
import { playNivelSound, unlockAudioOnFirstGesture } from 'components/rebrand/sfx';

/**
 * Destaque "Você foi reconhecido": carrossel dos reconhecimentos recebidos no
 * ciclo, com animação e som por nível. Renderiza nada enquanto carrega ou se
 * não houver reconhecimentos recebidos.
 */
export function RecognitionHighlight() {
  const { user, currentCycle } = useAuth();
  const [received, setReceived] = useState<any[]>([]);
  const [index, setIndex] = useState(0);
  const [playKey, setPlayKey] = useState(0);
  const [openComment, setOpenComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [loadingInteraction, setLoadingInteraction] = useState(false);

  useEffect(() => {
    unlockAudioOnFirstGesture();
  }, []);

  useEffect(() => {
    async function loadReceived() {
      try {
        const data = await getKPIHistoryPag(user?._id, {}, 0, 10, currentCycle?.date_start, currentCycle?.date_finish);
        const valid = (Array.isArray(data?.result) ? data.result : [])
          .filter((item: any) => !item.cancelled)
          .sort((a: any, b: any) => new Date(b.time).getTime() - new Date(a.time).getTime());
        setReceived(valid);
      } catch (e) {
        setReceived([]);
      }
    }

    if (!!user && !!currentCycle) {
      loadReceived();
    }
  }, [user, currentCycle]);

  const current = received[index];
  const nivel = useMemo(() => nivelByMoeda(current?.coin), [current]);
  const dimensao = useMemo(() => dimensaoByBehavior(current?.behavior), [current]);

  useEffect(() => {
    if (!current) return;
    setPlayKey(key => key + 1);
    try {
      playNivelSound(nivelByMoeda(current.coin).id);
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, received.length]);

  if (!current) return null;

  const accent = dimensao.color;
  const senderUnidade = current.sender_desc_unidade || current.sender_diretoria || current.sender_nome_dependencia;

  function navigateBy(delta: number) {
    setOpenComment(false);
    setCommentDraft('');
    setIndex(prev => (prev + delta + received.length) % received.length);
  }

  function replay() {
    setPlayKey(key => key + 1);
    try {
      playNivelSound(nivel.id);
    } catch (e) {}
  }

  function hasLike(item: any) {
    return !!item?.interaction?.find((i: any) => i.sender === user?._id && !!i.like);
  }

  function likeCount(item: any) {
    return (item?.interaction || []).filter((i: any) => !!i.like).length;
  }

  function getComment(item: any) {
    return item?.interaction?.find((i: any) => !!i.message?.trim().length) || null;
  }

  // Curtir/comentar o reconhecimento recebido — mesma lógica de "Meus
  // Reconhecimentos" (interactions__c via registerInteraction), só que aqui na
  // Página Inicial. Não são curtidas diferentes: é o mesmo registro por usuário.
  async function sendInteraction(item: any, type: 'like' | 'message', message?: string) {
    if (!item?.action_id || loadingInteraction) return;
    setLoadingInteraction(true);

    const idx = received.findIndex(r => r.action_id === item.action_id);
    const newList = [...received];
    const interactionItem = item.interaction?.[0];

    let payload: any;
    if (!item?.interaction?.length) {
      payload = {
        item: item.action_id,
        player: item.sender,
        sender: user?._id,
        senderName: user?.name,
        like: type === 'like' ? !hasLike(item) : hasLike(item),
        message: type === 'message' && message?.trim().length ? message.trim() : undefined,
        created: { $date: new Date().getTime() },
      };
    } else if (interactionItem?._id) {
      payload = {
        ...interactionItem,
        like: type === 'like' ? !hasLike(item) : interactionItem.like,
        message: type === 'message' ? message : interactionItem.message,
        updated: { $date: new Date().getTime() },
      };
      if (typeof payload.created === 'number') payload.created = { $date: payload.created };
    }

    if (payload) {
      const data = await registerInteraction(payload);
      if (idx >= 0) {
        newList[idx].interaction = [data];
        setReceived(newList);
      }
    }
    setLoadingInteraction(false);
  }

  async function submitComment() {
    const text = commentDraft.trim();
    if (!text || loadingInteraction) return;
    await sendInteraction(current, 'message', text);
    setOpenComment(false);
    setCommentDraft('');
  }

  const comment = getComment(current);

  return (
    <section
      className="rb-spot rb-rise"
      style={{
        background: `linear-gradient(150deg, ${accent}, rgba(40,130,210,.28) 45%, rgba(255,255,255,.05))`,
      }}
    >
      <div className="rb-spot__inner">
        <div className="rb-spot__watermark">
          <IconLeafSeed color={accent} size={150} />
        </div>

        <div className="rb-spot__topbar">
          <div className="rb-spot__live">
            <span className="rb-livedot" style={{ background: accent }} />
            <span className="rb-spot__live-label">Você foi reconhecido</span>
          </div>

          {received.length > 1 && (
            <div className="rb-spot__nav">
              <button type="button" className="rb-spot__navbtn" onClick={() => navigateBy(-1)} aria-label="Anterior">
                ‹
              </button>
              <span className="rb-spot__counter">
                {index + 1}/{received.length}
              </span>
              <button type="button" className="rb-spot__navbtn" onClick={() => navigateBy(1)} aria-label="Próximo">
                ›
              </button>
            </div>
          )}
        </div>

        <div className="rb-spot-grid">
          <div className="rb-spot__sender">
            <div className="rb-spot__sender-avatar">
              <div className="rb-spot__avatar-ring" style={{ boxShadow: `0 0 0 2px ${accent}, 0 0 26px ${hexToRgba(accent, 0.5)}` }}>
                <UserAvatar data={{ ...current.sender_avatar, ariaHidden: true }} responsive={true} />
              </div>
              <div className="rb-seedbadge" style={{ background: accent }}>
                <IconSprout color="#fff" size={14} />
              </div>
            </div>
            <Humanograma data={{ ...current, nome: current.sender_name, funcao: current.sender_funcao, _id: current.sender }}>
              <div className="rb-spot__sender-name">
                {current.sender_nome_guerra || current.sender_name?.split(' ')[0]}
              </div>
            </Humanograma>
            {!!senderUnidade && <div className="rb-spot__sender-unit">{senderUnidade}</div>}
            {!!current.sender_funcao && <div className="rb-spot__sender-role">{current.sender_funcao}</div>}
            <div className="rb-spot__sender-hint">reconheceu você</div>
          </div>

          <div className="rb-spot__message">
            <button
              type="button"
              className="rb-replay"
              onClick={replay}
              title="Reproduzir animação e som"
              style={{ background: hexToRgba(accent, 0.14), boxShadow: `inset 0 0 0 1px ${hexToRgba(accent, 0.4)}` }}
            >
              <span key={playKey} className="rb-replay__anim">
                <NivelAnimation nivel={nivel.id} size={74} />
              </span>
              <span className="rb-replay__icon" style={{ color: accent }}>
                <IconReplay size={12} />
              </span>
            </button>

            <div className="rb-spot__message-body">
              <div className="rb-spot__message-tags">
                <span className="rb-spot__nivel-tag">{nivel.tag}</span>
                <span
                  className="rb-dimchip rb-dimchip--dark"
                  style={{ color: dimensao.color, background: hexToRgba(dimensao.color, 0.14), boxShadow: `inset 0 0 0 1.5px ${hexToRgba(dimensao.color, 0.5)}` }}
                >
                  <span className="rb-dimchip__dot" style={{ background: dimensao.color }} />
                  {dimensao.label}
                </span>
              </div>
              {!!current.message && (
                <div className="rb-spot__message-text" dangerouslySetInnerHTML={{ __html: convertEmojis(current.message) }} />
              )}
              <div className="rb-spot__message-date">{format(new Date(current.time), 'dd/MM - HH:mm')}</div>

              {!!comment && (
                <div className="rb-spot__comment">
                  <span className="rb-spot__comment-label">Seu comentário</span>
                  <span dangerouslySetInnerHTML={{ __html: convertEmojis(comment.message) }} />
                </div>
              )}

              {!!current.action_id && (
                <div className="rb-spot__actions">
                  <button
                    type="button"
                    className={`rb-spot__social ${hasLike(current) ? 'is-liked' : ''}`}
                    onClick={() => sendInteraction(current, 'like')}
                    disabled={loadingInteraction}
                  >
                    <FaHeart size={14} /> {likeCount(current)} Curtir
                  </button>
                  <button
                    type="button"
                    className="rb-spot__social"
                    onClick={() => {
                      setCommentDraft(comment?.message || '');
                      setOpenComment(o => !o);
                    }}
                    disabled={loadingInteraction}
                  >
                    <MdModeComment size={15} /> Comentar
                  </button>
                </div>
              )}

              {openComment && (
                <div className="rb-spot__commentbox">
                  <input
                    className="rb-input"
                    value={commentDraft}
                    autoFocus
                    onChange={e => setCommentDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') submitComment();
                    }}
                    placeholder="Escreva um comentário..."
                  />
                  <button type="button" className="rb-btn-primary" disabled={loadingInteraction} onClick={submitComment}>
                    Enviar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rb-spot__footer">
          <Link to="/extrato" className="rb-spotlink">
            Ver meus reconhecimentos →
          </Link>
        </div>
      </div>
    </section>
  );
}
